import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCart } from "../../utils/cartContext";
import { useAuth } from "../../utils/AuthContext";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  AppBar,
  Toolbar,
  Divider,
  Paper,
  useMediaQuery,
  Badge,
  Slide,
} from "@mui/material";
import {
  Search,
  Favorite,
  FavoriteBorder,
  Sort,
  Close,
  Star,
  LocationOn,
  CurrencyRupee,
  SwapVert,
  Check,
  ArrowUpward,
  ArrowDownward,
  StarOutline,
  NearMe,
  NearMeOutlined,
  ArrowBack,
  FilterList,
  LocalOffer,
  Tune,
  ClearAll,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import { url } from "../../Constant/TextConstant";
import Header from "../Header";
import Footer from "../Footer/Footer";
import { useUserLocation } from "../../utils/useUserLocation";

// Color Constants
const PRIMARY_COLOR = "#ed7a07";
const SECONDARY_COLOR = "#2d2d2d";
const ACCENT_COLOR = "#ff9900";
const BACKGROUND_COLOR = "#ffffff";
const CARD_BACKGROUND = "#f9f9f9";
const TEXT_PRIMARY = "#131921";
const TEXT_SECONDARY = "#565959";
const DISCOUNT_COLOR = "#cc0c39";
const RATING_COLOR = "#ffa41c";

// Animation Constants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

const slideUp = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
};

const popIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 300 },
  },
};

// Constants and Enums
const API_BASE_URL = url.baseAPIUrl;

const SortOption = {
  NONE: "none",
  DISTANCE_NEAR_TO_FAR: "distance_near_to_far",
  DISTANCE_FAR_TO_NEAR: "distance_far_to_near",
  PRICE_LOW_TO_HIGH: "price_low_to_high",
  PRICE_HIGH_TO_LOW: "price_high_to_low",
  RATING_HIGH_TO_LOW: "rating_high_to_low",
  RATING_LOW_TO_HIGH: "rating_low_to_high",
};

const FilterOption = {
  PRICE: "price",
  RATING: "rating",
  DISTANCE: "distance",
};

const PriceRange = {
  UPTO_500: "upto_500",
  FIVE_HUNDRED_TO_1000: "500_1000",
  THOUSAND_TO_5000: "1000_5000",
  ABOVE_5000: "above_5000",
};

const RatingRange = {
  FIVE: "5",
  FOUR_TO_FIVE: "4_5",
  THREE_TO_FOUR: "3_4",
  BELOW_THREE: "below_3",
};

const DistanceRange = {
  WITHIN_200M: "within_200m",
  TWO_HUNDRED_TO_500M: "200_500m",
  FIVE_HUNDRED_TO_1000M: "500m_1km",
  ABOVE_1000M: "above_1km",
};

// Styled Components
const ProductCard = styled(Card)(({ theme }) => ({
  transition: "transform 0.3s, box-shadow 0.3s",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: CARD_BACKGROUND,
  borderRadius: "12px",
  overflow: "hidden",
  border: "1px solid #e7e7e7",
  minHeight: "300px",
  width: "100%",
  maxWidth: "200px",
  margin: "0 auto",
  [theme.breakpoints.down("sm")]: {
    width: "250px",
  },
  cursor: "pointer", // Add pointer cursor to indicate clickable
}));

const ProductImageContainer = styled(Box)({
  position: "relative",
  height: "200px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "white",
  padding: "4px",
});

const ProductImage = styled("img")({
  maxHeight: "100%",
  maxWidth: "100%",
  objectFit: "contain",
});

const PriceText = styled(Typography)({
  color: DISCOUNT_COLOR,
  fontWeight: "bold",
  fontSize: "1rem",
});


const DiscountBadge = styled(Box)({
  position: "absolute",
  top: 8,
  right: 8,
  backgroundColor: DISCOUNT_COLOR,
  color: "white",
  padding: "2px 8px",
  borderRadius: "12px",
  fontSize: "0.75rem",
  fontWeight: "bold",
  zIndex: 2,
  transform: "scale(1)",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
});


const DistanceChip = styled(Chip)({
  backgroundColor: SECONDARY_COLOR,
  color: "white",
  marginLeft: "auto",
  fontSize: "0.7rem",
  height: "22px",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
});
const RatingChip = styled(Chip)({
  backgroundColor: RATING_COLOR,
  color: TEXT_PRIMARY,
  fontWeight: "bold",
  position: "absolute",
  top: 8,
  left: 8,
  padding: "2px 4px",
  fontSize: "0.75rem",
  height: "24px",
  zIndex: 2,
});



const FilterButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  borderColor: active ? PRIMARY_COLOR : "#ddd",
  backgroundColor: active ? `${PRIMARY_COLOR}15` : "white",
  color: active ? PRIMARY_COLOR : TEXT_SECONDARY,
  marginRight: "8px",
  textTransform: "none",
  borderRadius: "20px",
  padding: "6px 16px",
  minWidth: "unset",
  fontSize: "0.85rem",
  fontWeight: active ? 600 : 500,
  boxShadow: active ? `0 2px 8px ${PRIMARY_COLOR}20` : "none",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: active ? `${PRIMARY_COLOR}25` : "#f5f5f5",
    borderColor: active ? PRIMARY_COLOR : "#ccc",
    transform: "translateY(-1px)",
    boxShadow: active
      ? `0 4px 12px ${PRIMARY_COLOR}30`
      : "0 2px 8px rgba(0,0,0,0.1)",
  },
}));

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    padding: "16px",
    maxWidth: "500px",
    overflow: "hidden",
  },
  "& .MuiDialogTitle-root": {
    paddingBottom: "8px",
    borderBottom: `1px solid ${PRIMARY_COLOR}20`,
  },
});



const FilterChip = styled(Chip)({
  borderRadius: "16px",
  padding: "8px 12px",
  fontSize: "0.875rem",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.03)",
  },
});

const FilterSection = styled(Paper)({
  padding: "12px 16px",
  marginBottom: "16px",
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: BACKGROUND_COLOR,
  borderBottom: `1px solid #e7e7e7`,
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
});

// Main Component
const CategoryProductScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const { subcategory, imageUrl } = location.state || {};
  const searchTerm = subcategory || "";

  // State
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [favorites, setFavorites] = useState({});
  const [currentPosition, setCurrentPosition] = useState(null);

  // Filter state
  const [selectedPriceRange, setSelectedPriceRange] = useState(null);
  const [selectedRatingRange, setSelectedRatingRange] = useState(null);
  const [selectedDistanceRange, setSelectedDistanceRange] = useState(null);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [currentFilterDialog, setCurrentFilterDialog] = useState(null);
  const [currentSortOption, setCurrentSortOption] = useState(SortOption.NONE);
  const [showSortDialog, setShowSortDialog] = useState(false);
  const [reviews, setReviews] = useState([]);
  const { addToCart: contextAddToCart } = useCart();
  const { currentUser } = useAuth();

  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };
  useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  const handleProductClick = (product) => {
    // Navigate to product detail page or show more info
    const slug = createProductSlug(product.ProductName);
    navigate(`/product/${product.ID}/${slug}`, {
      state: { product },
    });
  };
  // Enhanced slug creation function
  const createProductSlug = (productName) => {
    return (
      productName
        .toLowerCase()
        .trim()
        // First replace % with '-percent-' to make it readable
        .replace(/%/g, "-percent-")
        // Then replace + with '-plus-'
        .replace(/\+/g, "-plus-")
        // Remove other special characters except hyphens
        .replace(/[^\w\s-]/g, "")
        // Replace spaces with hyphens
        .replace(/\s+/g, "-")
        // Collapse multiple hyphens
        .replace(/-+/g, "-")
        // Limit length
        .slice(0, 60)
        // Remove trailing hyphen
        .replace(/-$/, "")
    );
  };

  // Fetch products with error handling
  const fetchProducts = useCallback(
    async (latitude, longitude) => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const mobile = localStorage.getItem("userMobile") || "";
        const userRole = "Customer";
        const url = `${API_BASE_URL}/api/BindingProducts/GetSubCategoryProducts?userRole=${userRole}&searchterm=${encodeURIComponent(
          searchTerm
        )}&mobile=${mobile}&userLatitude=${latitude}&userLongitude=${longitude}`;

        const response = await axios.get(url);
        if (response?.status === 200) {
          const data = Array.isArray(response?.data) ? response.data : [];
          setProducts(data);
          setFilteredProducts(data);
        } else {
          throw new Error("Failed to load products");
        }
      } catch (error) {
        setErrorMessage(`No Products are available in ${searchTerm} category`);
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setIsLoading(false);
      }
    },
    [searchTerm]
  );

  useEffect(() => {
    if (!searchTerm) {
      setErrorMessage("No subcategory selected");
      setIsLoading(false);
      return;
    }
    getCurrentLocation();
  }, [searchTerm]);

  const handleAddToCart = useCallback(
    (product, e) => {
      if (product.StoreMobile === currentUser) {
        const userConfirmed = window.confirm(
          "This is your own product. You can't add it to the cart."
        );
        return;
      }
      e.stopPropagation();
      const requiredFields = [
        { key: "ProductCode", name: "Product Code" },
        { key: "ProductName", name: "Product Name" },
        { key: "OurPrice", name: "Price" },
        { key: "StoreMobile", name: "Store Mobile" },
        { key: "StoreName", name: "Store Name" },
      ];

      const missingFields = requiredFields.filter(
        (field) => !product?.[field.key]
      );

      if (missingFields.length > 0) {
        toast.error(
          <div>
            Missing product information:
            <ul style={{ marginTop: "5px", paddingLeft: "20px" }}>
              {missingFields.map((field) => (
                <li key={field.key}>{field.name}</li>
              ))}
            </ul>
            Please try again later.
          </div>,
          {
            position: "bottom-right",
            autoClose: 5000,
            style: { backgroundColor: "#ff4444", color: "white" },
          }
        );
        return;
      }

      const productReviews = reviews.filter(
        (review) => review.ProductCode === product.ProductCode
      );

      const productToAdd = {
        ID: product?.ID || product?.ProductCode || 0,
        ProductName: product?.ProductName || "",
        ProductCode: product?.ProductCode || "",
        OurPrice: product?.OurPrice || 0,
        Mrp: product?.Mrp || 0,
        DiscountPercentage: product?.Discount || 0,
        Image1: product?.Image1 || "",
        Image2: product?.Image2 || "",
        StoreMobile: product?.StoreMobile || "",
        StoreName: product?.StoreName || "",
        Category: product?.Category || "",
        SubCategory1: product?.SubCategory1 || "",
        SubCategory2: product?.SubCategory2 || "",
        Cashback: product?.Cashback || 0,
        Size: product?.Size || "",
        ColorName: product?.ColorName || "",
        quantity: 1,
        Total: (product?.OurPrice || 0) * 1,
        Latitude: product?.Latitude || 0,
        Longitude: product?.Longitude || 0,
        AverageRating: product?.AverageRating || 0,
        ReviewCount: productReviews?.length || 0,
        stock: product?.Stock || 10,
        addedAt: new Date().toISOString(),
      };

      contextAddToCart(productToAdd);
    },
    [reviews, contextAddToCart]
  );

  const getCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setErrorMessage("Geolocation is not supported by your browser");
      fetchProducts(0, 0);
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          maximumAge: 60000,
        });
      });

      setCurrentPosition({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });

      fetchProducts(position.coords.latitude, position.coords.longitude);
    } catch (error) {
      setErrorMessage("Unable to retrieve your location");
      fetchProducts(0, 0);
    }
  }, [fetchProducts]);

  // Fixed filter and sort functionality
  const applyFilters = useCallback(() => {
    let filtered = [...products];

    // Apply price filter
    if (selectedPriceRange) {
      filtered = filtered.filter((product) => {
        const price = parseFloat(product?.OurPrice) || 0; // Fixed property name (OurPrice instead of ourPrice)
        switch (selectedPriceRange) {
          case PriceRange.UPTO_500:
            return price <= 500;
          case PriceRange.FIVE_HUNDRED_TO_1000:
            return price > 500 && price <= 1000;
          case PriceRange.THOUSAND_TO_5000:
            return price > 1000 && price <= 5000;
          case PriceRange.ABOVE_5000:
            return price > 5000;
          default:
            return true;
        }
      });
    }

    // Apply rating filter
    if (selectedRatingRange) {
      filtered = filtered.filter((product) => {
        const rating = parseFloat(product?.AverageRating) || 0; // Fixed property name (AverageRating instead of averageRating)
        switch (selectedRatingRange) {
          case RatingRange.FIVE:
            return rating === 5;
          case RatingRange.FOUR_TO_FIVE:
            return rating >= 4;
          case RatingRange.THREE_TO_FOUR:
            return rating >= 3 && rating < 4;
          case RatingRange.BELOW_THREE:
            return rating < 3;
          default:
            return true;
        }
      });
    }

    // Apply distance filter
    if (selectedDistanceRange) {
      filtered = filtered.filter((product) => {
        const distance = parseFloat(product?.Awaykm) || 0; // Fixed property name (Awaykm instead of awaykm)
        switch (selectedDistanceRange) {
          case DistanceRange.WITHIN_200M:
            return distance <= 0.2;
          case DistanceRange.TWO_HUNDRED_TO_500M:
            return distance > 0.2 && distance <= 0.5;
          case DistanceRange.FIVE_HUNDRED_TO_1000M:
            return distance > 0.5 && distance <= 1;
          case DistanceRange.ABOVE_1000M:
            return distance > 1;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    switch (currentSortOption) {
      case SortOption.DISTANCE_NEAR_TO_FAR:
        filtered.sort(
          (a, b) => (parseFloat(a?.Awaykm) || 0) - (parseFloat(b?.Awaykm) || 0)
        );
        break;
      case SortOption.DISTANCE_FAR_TO_NEAR:
        filtered.sort(
          (a, b) => (parseFloat(b?.Awaykm) || 0) - (parseFloat(a?.Awaykm) || 0)
        );
        break;
      case SortOption.PRICE_LOW_TO_HIGH:
        filtered.sort((a, b) => {
          const priceA = parseFloat(a?.OurPrice) || 0;
          const priceB = parseFloat(b?.OurPrice) || 0;
          return priceA - priceB;
        });
        break;
      case SortOption.PRICE_HIGH_TO_LOW:
        filtered.sort((a, b) => {
          const priceA = parseFloat(a?.OurPrice) || 0;
          const priceB = parseFloat(b?.OurPrice) || 0;
          return priceB - priceA;
        });
        break;
      case SortOption.RATING_HIGH_TO_LOW:
        filtered.sort(
          (a, b) =>
            (parseFloat(b?.AverageRating) || 0) -
            (parseFloat(a?.AverageRating) || 0)
        );
        break;
      case SortOption.RATING_LOW_TO_HIGH:
        filtered.sort(
          (a, b) =>
            (parseFloat(a?.AverageRating) || 0) -
            (parseFloat(b?.AverageRating) || 0)
        );
        break;
      default:
        break;
    }

    setFilteredProducts(filtered);
  }, [
    products,
    selectedPriceRange,
    selectedRatingRange,
    selectedDistanceRange,
    currentSortOption,
  ]);

  // Apply filters when dependencies change
  useEffect(() => {
    if (products.length > 0) {
      applyFilters();
    }
  }, [applyFilters, products.length]);

  const toggleFavorite = (productId) => {
    setFavorites((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const calculateDiscount = (ourPrice, mrp) => {
    if (!ourPrice || !mrp) return 0;
    const discount = ((mrp - ourPrice) / mrp) * 100;
    return Math.round(discount);
  };

  const renderFilterChips = () => {
    switch (currentFilterDialog) {
      case FilterOption.PRICE:
        return [
          { label: "Under ₹500", value: PriceRange.UPTO_500 },
          { label: "₹500-1K", value: PriceRange.FIVE_HUNDRED_TO_1000 },
          { label: "₹1K-5K", value: PriceRange.THOUSAND_TO_5000 },
          { label: "Over ₹5K", value: PriceRange.ABOVE_5000 },
        ];
      case FilterOption.RATING:
        return [
          { label: "5 ★", value: RatingRange.FIVE },
          { label: "4-5 ★", value: RatingRange.FOUR_TO_FIVE },
          { label: "3-4 ★", value: RatingRange.THREE_TO_FOUR },
          { label: "Below 3 ★", value: RatingRange.BELOW_THREE },
        ];
      case FilterOption.DISTANCE:
        return [
          { label: "<200m", value: DistanceRange.WITHIN_200M },
          { label: "200-500m", value: DistanceRange.TWO_HUNDRED_TO_500M },
          { label: "500m-1km", value: DistanceRange.FIVE_HUNDRED_TO_1000M },
          { label: ">1km", value: DistanceRange.ABOVE_1000M },
        ];
      default:
        return [];
    }
  };

  const renderSortOptions = () => [
    {
      icon: <NearMe />,
      label: "Nearby",
      value: SortOption.DISTANCE_NEAR_TO_FAR,
    },
    {
      icon: <NearMeOutlined />,
      label: "Farthest",
      value: SortOption.DISTANCE_FAR_TO_NEAR,
    },
    {
      icon: <ArrowUpward />,
      label: "Price Low to High",
      value: SortOption.PRICE_LOW_TO_HIGH,
    },
    {
      icon: <ArrowDownward />,
      label: "Price High to Low",
      value: SortOption.PRICE_HIGH_TO_LOW,
    },
    {
      icon: <Star />,
      label: "Top Rated",
      value: SortOption.RATING_HIGH_TO_LOW,
    },
    {
      icon: <StarOutline />,
      label: "Low Rated",
      value: SortOption.RATING_LOW_TO_HIGH,
    },
  ];

  const handleFilterSelect = (value) => {
    switch (currentFilterDialog) {
      case FilterOption.PRICE:
        setSelectedPriceRange(selectedPriceRange === value ? null : value);
        break;
      case FilterOption.RATING:
        setSelectedRatingRange(selectedRatingRange === value ? null : value);
        break;
      case FilterOption.DISTANCE:
        setSelectedDistanceRange(
          selectedDistanceRange === value ? null : value
        );
        break;
      default:
        break;
    }
  };

  const handleSortSelect = (value) => {
    setCurrentSortOption(value);
    setShowSortDialog(false);
  };

  const clearAllFilters = () => {
    setSelectedPriceRange(null);
    setSelectedRatingRange(null);
    setSelectedDistanceRange(null);
    setCurrentSortOption(SortOption.NONE);
  };

  const hasActiveFilters =
    selectedPriceRange ||
    selectedRatingRange ||
    selectedDistanceRange ||
    currentSortOption !== SortOption.NONE;

  return (
    <>
      <Helmet>
        <title>
          {searchTerm
            ? `Buy ${searchTerm} Online | Best Prices & Deals`
            : "Shop Products Online"}
        </title>
        <meta
          name="description"
          content={
            searchTerm
              ? `Shop for ${searchTerm} at best prices. Find great deals, discounts, and nearby stores. Free delivery available.`
              : "Discover a wide range of products at competitive prices. Shop from local stores with fast delivery options."
          }
        />
        <meta
          name="keywords"
          content={`${searchTerm}, buy ${searchTerm} online, ${searchTerm} near me, ${searchTerm} price, best ${searchTerm}, shop ${searchTerm}, online shopping`}
        />
        <meta
          property="og:title"
          content={
            searchTerm
              ? `Buy ${searchTerm} Online | Best Deals`
              : "Shop Products Online"
          }
        />
        <meta
          property="og:description"
          content={
            searchTerm
              ? `Find the best ${searchTerm} products at great prices. Shop now for exclusive deals and fast delivery.`
              : "Discover thousands of products from local stores with competitive prices and fast delivery."
          }
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        {imageUrl && (
          <meta
            property="og:image"
            content={`${url.productImageStartingPath}/${imageUrl}`}
          />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            searchTerm
              ? `Buy ${searchTerm} Online | Best Prices`
              : "Shop Products Online"
          }
        />
        <meta
          name="twitter:description"
          content={
            searchTerm
              ? `Shop for ${searchTerm} with great deals and discounts. Find nearby stores and compare prices.`
              : "Discover products from local stores with competitive prices and fast delivery options."
          }
        />
        {imageUrl && (
          <meta
            name="twitter:image"
            content={`${url.productImageStartingPath}/${imageUrl}`}
          />
        )}
      </Helmet>
      <Header
        userLocation={currentLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <div
        style={{
          backgroundColor: BACKGROUND_COLOR,
          margin: "0px auto 10px auto",
        }}
      >
        {/* Enhanced Filter Section with Animations */}
        <FilterSection
          elevation={0}
          component={motion.div}
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <FilterButton
              variant="outlined"
              startIcon={<Tune fontSize="small" />}
              active={hasActiveFilters}
              onClick={() => setShowSortDialog(true)}
              component={motion.div}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              {isMobile ? "Sort & Filter" : "Sort & Filter"}
            </FilterButton>

            <FilterButton
              variant="outlined"
              startIcon={<CurrencyRupee fontSize="small" />}
              active={!!selectedPriceRange}
              onClick={() => {
                setCurrentFilterDialog(FilterOption.PRICE);
                setShowFilterDialog(true);
              }}
              component={motion.div}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              {isMobile ? "Price" : "Price Range"}
            </FilterButton>

            <FilterButton
              variant="outlined"
              startIcon={<Star fontSize="small" />}
              active={!!selectedRatingRange}
              onClick={() => {
                setCurrentFilterDialog(FilterOption.RATING);
                setShowFilterDialog(true);
              }}
              component={motion.div}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              {isMobile ? "Rating" : "Customer Rating"}
            </FilterButton>

            <FilterButton
              variant="outlined"
              startIcon={<LocationOn fontSize="small" />}
              active={!!selectedDistanceRange}
              onClick={() => {
                setCurrentFilterDialog(FilterOption.DISTANCE);
                setShowFilterDialog(true);
              }}
              component={motion.div}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              {isMobile ? "Nearby" : "Distance"}
            </FilterButton>

            {hasActiveFilters && (
              <Button
                variant="text"
                sx={{
                  ml: "auto",
                  color: DISCOUNT_COLOR,
                  fontSize: "0.8rem",
                  minWidth: "unset",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
                size="small"
                onClick={clearAllFilters}
                startIcon={<ClearAll fontSize="small" />}
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Clear all
              </Button>
            )}
          </Box>

          {/* Active Filters Indicator */}
          <AnimatePresence>
            {hasActiveFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 1,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: TEXT_SECONDARY, fontSize: "0.75rem" }}
                  >
                    Active filters:
                  </Typography>
                  {selectedPriceRange && (
                    <Chip
                      label={
                        selectedPriceRange === PriceRange.UPTO_500
                          ? "Under ₹500"
                          : selectedPriceRange ===
                            PriceRange.FIVE_HUNDRED_TO_1000
                          ? "₹500-1K"
                          : selectedPriceRange === PriceRange.THOUSAND_TO_5000
                          ? "₹1K-5K"
                          : "Over ₹5K"
                      }
                      size="small"
                      onDelete={() => setSelectedPriceRange(null)}
                      sx={{
                        backgroundColor: `${PRIMARY_COLOR}15`,
                        color: PRIMARY_COLOR,
                      }}
                      component={motion.div}
                      whileHover={{ scale: 1.05 }}
                    />
                  )}
                  {selectedRatingRange && (
                    <Chip
                      label={
                        selectedRatingRange === RatingRange.FIVE
                          ? "5 ★"
                          : selectedRatingRange === RatingRange.FOUR_TO_FIVE
                          ? "4-5 ★"
                          : selectedRatingRange === RatingRange.THREE_TO_FOUR
                          ? "3-4 ★"
                          : "Below 3 ★"
                      }
                      size="small"
                      onDelete={() => setSelectedRatingRange(null)}
                      sx={{
                        backgroundColor: `${PRIMARY_COLOR}15`,
                        color: PRIMARY_COLOR,
                      }}
                      component={motion.div}
                      whileHover={{ scale: 1.05 }}
                    />
                  )}
                  {selectedDistanceRange && (
                    <Chip
                      label={
                        selectedDistanceRange === DistanceRange.WITHIN_200M
                          ? "<200m"
                          : selectedDistanceRange ===
                            DistanceRange.TWO_HUNDRED_TO_500M
                          ? "200-500m"
                          : selectedDistanceRange ===
                            DistanceRange.FIVE_HUNDRED_TO_1000M
                          ? "500m-1km"
                          : ">1km"
                      }
                      size="small"
                      onDelete={() => setSelectedDistanceRange(null)}
                      sx={{
                        backgroundColor: `${PRIMARY_COLOR}15`,
                        color: PRIMARY_COLOR,
                      }}
                      component={motion.div}
                      whileHover={{ scale: 1.05 }}
                    />
                  )}
                  {currentSortOption !== SortOption.NONE && (
                    <Chip
                      label={
                        currentSortOption === SortOption.DISTANCE_NEAR_TO_FAR
                          ? "Nearby"
                          : currentSortOption ===
                            SortOption.DISTANCE_FAR_TO_NEAR
                          ? "Farthest"
                          : currentSortOption === SortOption.PRICE_LOW_TO_HIGH
                          ? "Price Low-High"
                          : currentSortOption === SortOption.PRICE_HIGH_TO_LOW
                          ? "Price High-Low"
                          : currentSortOption === SortOption.RATING_HIGH_TO_LOW
                          ? "Top Rated"
                          : "Low Rated"
                      }
                      size="small"
                      onDelete={() => setCurrentSortOption(SortOption.NONE)}
                      sx={{
                        backgroundColor: `${PRIMARY_COLOR}15`,
                        color: PRIMARY_COLOR,
                      }}
                      component={motion.div}
                      whileHover={{ scale: 1.05 }}
                    />
                  )}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </FilterSection>

        {/* Search Header */}
        <Container sx={{ mb: 2, px: isMobile ? 1 : 3 }}>
          <Typography
            variant="body1"
            color={TEXT_SECONDARY}
            sx={{ fontSize: "0.9rem" }}
          >
            Results for{" "}
            <Typography
              component="span"
              sx={{ color: PRIMARY_COLOR, fontWeight: "bold" }}
            >
              "{searchTerm}"
            </Typography>{" "}
            ({filteredProducts.length}{" "}
            {filteredProducts.length === 1 ? "item" : "items"})
          </Typography>
        </Container>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: PRIMARY_COLOR }} />
          </Box>
        )}

        {/* Error State */}
        {errorMessage && !isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <Typography sx={{ color: DISCOUNT_COLOR }}>
              {errorMessage}
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && !errorMessage && filteredProducts.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
              textAlign: "center",
              px: 3,
            }}
          >
            <Search
              style={{
                fontSize: 60,
                color: PRIMARY_COLOR,
                marginBottom: 16,
              }}
            />
            <Typography variant="h6" color={TEXT_PRIMARY} gutterBottom>
              No Products Found
            </Typography>
            <Typography variant="body2" color={TEXT_SECONDARY} sx={{ mb: 2 }}>
              We couldn't find any matches for{" "}
              <span style={{ fontWeight: "bold" }}>"{searchTerm}"</span>
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: PRIMARY_COLOR,
                color: "white",
                "&:hover": {
                  backgroundColor: `${PRIMARY_COLOR}cc`,
                },
              }}
              onClick={clearAllFilters}
            >
              Clear Filters
            </Button>
          </Box>
        )}

        {/* Product Grid with fixed card sizes */}
        {!isLoading && !errorMessage && filteredProducts.length > 0 && (
          <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
              {filteredProducts.map((product, index) => {
                const discount = calculateDiscount(
                  product.OurPrice,
                  product.Mrp
                );
                return (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2.4}
                    xl={2}
                    key={product.ID || `${product.ProductName}-${index}`}
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <ProductCard onClick={() => handleProductClick(product)}>
                      <ProductImageContainer>
                        <ProductImage
                          src={
                            `${url.productImageStartingPath}/${product.Image1}` ||
                            "https://via.placeholder.com/200?text=No+Image"
                          }
                          alt={product.ProductName || "Product"}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/200?text=No+Image";
                          }}
                        />
                        {product.AverageRating > 0 && (
                          <RatingChip
                            label={`${(product.AverageRating || 0).toFixed(
                              1
                            )} ★`}
                            size="small"
                          />
                        )}
                        {discount > 0 && (
                          <DiscountBadge>{discount}% off</DiscountBadge>
                        )}
                      </ProductImageContainer>

                      <CardContent
                        sx={{
                          flexGrow: 1,
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          gutterBottom
                          variant="body1"
                          component="div"
                          sx={{
                            fontWeight: 500,
                            fontSize: "1.1rem",
                            color: TEXT_PRIMARY,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: "44px",
                          }}
                        >
                          {product.BrandName || "Generic"}{" "}
                          {product.ProductName || "Unnamed Product"}
                        </Typography>

                        <Typography
                          variant="body2"
                          color={TEXT_SECONDARY}
                        
                          sx={{
                            mb: 1,
                            fontSize: "0.8rem",
                            height: "20px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                          }}
                        >
                          {product.StoreName || "Unknown Store"}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: "auto",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <PriceText>₹{product.OurPrice || "0"}</PriceText>
                          {product.Mrp && (
                            <Typography
                              variant="body2"
                              color={TEXT_SECONDARY}
                              sx={{
                                textDecoration: "line-through",
                                fontSize: "0.8rem",
                              }}
                            >
                              ₹{product.Mrp}
                            </Typography>
                          )}
                          {product.Awaykm && (
                            <DistanceChip
                              label={`${((product.Awaykm || 0) * 1000).toFixed(
                                0
                              )}m`}
                              size="small"
                              icon={<LocationOn fontSize="small" />}
                            />
                          )}
                        </Box>

                        <Button
                          onClick={(e) => handleAddToCart(product, e)}
                          fullWidth
                          variant="contained"
                          sx={{
                            mt: 1.5,
                            backgroundColor: PRIMARY_COLOR,
                            color: "white",
                            "&:hover": {
                              backgroundColor: `${PRIMARY_COLOR}cc`,
                            },
                            fontSize: "0.8rem",
                            py: 0.5,
                          }}
                          component={motion.div}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Add to Cart
                        </Button>
                      </CardContent>
                    </ProductCard>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        )}

        {/* Enhanced Filter Dialog with Animations */}
        <StyledDialog
          open={showFilterDialog}
          onClose={() => setShowFilterDialog(false)}
          fullWidth
          maxWidth="sm"
          TransitionComponent={Slide}
          transitionDuration={300}
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {currentFilterDialog === FilterOption.PRICE && (
                <CurrencyRupee sx={{ mr: 1, color: PRIMARY_COLOR }} />
              )}
              {currentFilterDialog === FilterOption.RATING && (
                <Star sx={{ mr: 1, color: PRIMARY_COLOR }} />
              )}
              {currentFilterDialog === FilterOption.DISTANCE && (
                <LocationOn sx={{ mr: 1, color: PRIMARY_COLOR }} />
              )}
              <Typography variant="h6" sx={{ color: TEXT_PRIMARY }}>
                {currentFilterDialog === FilterOption.PRICE && "Price Range"}
                {currentFilterDialog === FilterOption.RATING &&
                  "Customer Rating"}
                {currentFilterDialog === FilterOption.DISTANCE &&
                  "Distance from You"}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "center",
                py: 1,
              }}
            >
              {renderFilterChips().map((chip) => {
                const isSelected =
                  (currentFilterDialog === FilterOption.PRICE &&
                    selectedPriceRange === chip.value) ||
                  (currentFilterDialog === FilterOption.RATING &&
                    selectedRatingRange === chip.value) ||
                  (currentFilterDialog === FilterOption.DISTANCE &&
                    selectedDistanceRange === chip.value);

                return (
                  <FilterChip
                    key={chip.value}
                    label={chip.label}
                    clickable
                    sx={{
                      backgroundColor: isSelected
                        ? `${PRIMARY_COLOR}20`
                        : "inherit",
                      color: isSelected ? PRIMARY_COLOR : TEXT_SECONDARY,
                      borderColor: isSelected ? PRIMARY_COLOR : "#e0e0e0",
                      borderWidth: "1px",
                      borderStyle: "solid",
                    }}
                    variant={isSelected ? "outlined" : "outlined"}
                    onClick={() => handleFilterSelect(chip.value)}
                    icon={
                      isSelected ? (
                        <Check sx={{ color: PRIMARY_COLOR }} />
                      ) : null
                    }
                    component={motion.div}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  />
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                switch (currentFilterDialog) {
                  case FilterOption.PRICE:
                    setSelectedPriceRange(null);
                    break;
                  case FilterOption.RATING:
                    setSelectedRatingRange(null);
                    break;
                  case FilterOption.DISTANCE:
                    setSelectedDistanceRange(null);
                    break;
                  default:
                    break;
                }
                setShowFilterDialog(false);
              }}
              sx={{
                mr: 1,
                color: TEXT_SECONDARY,
                borderColor: "#e0e0e0",
                "&:hover": {
                  borderColor: "#bdbdbd",
                },
              }}
              component={motion.div}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: PRIMARY_COLOR,
                color: "white",
                "&:hover": {
                  backgroundColor: `${PRIMARY_COLOR}cc`,
                },
              }}
              onClick={() => setShowFilterDialog(false)}
              component={motion.div}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Apply
            </Button>
          </DialogActions>
        </StyledDialog>

        {/* Enhanced Sort Dialog with Animations */}
        <StyledDialog
          open={showSortDialog}
          onClose={() => setShowSortDialog(false)}
          fullWidth
          maxWidth="sm"
          TransitionComponent={Slide}
          transitionDuration={300}
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Sort sx={{ mr: 1, color: PRIMARY_COLOR }} />
              <Typography variant="h6" sx={{ color: TEXT_PRIMARY }}>
                Sort By
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={1}>
              {renderSortOptions().map((option) => (
                <Grid item xs={12} key={option.value}>
                  <Button
                    fullWidth
                    variant={
                      currentSortOption === option.value
                        ? "contained"
                        : "outlined"
                    }
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      py: 1.5,
                      borderRadius: 1,
                      backgroundColor:
                        currentSortOption === option.value
                          ? `${PRIMARY_COLOR}15`
                          : "inherit",
                      color:
                        currentSortOption === option.value
                          ? PRIMARY_COLOR
                          : TEXT_SECONDARY,
                      borderColor:
                        currentSortOption === option.value
                          ? PRIMARY_COLOR
                          : "#e0e0e0",
                      "&:hover": {
                        backgroundColor: `${PRIMARY_COLOR}10`,
                        borderColor: PRIMARY_COLOR,
                      },
                    }}
                    startIcon={React.cloneElement(option.icon, {
                      color:
                        currentSortOption === option.value
                          ? PRIMARY_COLOR
                          : "inherit",
                    })}
                    onClick={() => handleSortSelect(option.value)}
                    component={motion.div}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    {option.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setCurrentSortOption(SortOption.NONE);
                setShowSortDialog(false);
              }}
              sx={{
                mr: 1,
                color: TEXT_SECONDARY,
                borderColor: "#e0e0e0",
                "&:hover": {
                  borderColor: "#bdbdbd",
                },
              }}
              component={motion.div}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Clear Sort
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: PRIMARY_COLOR,
                color: "white",
                "&:hover": {
                  backgroundColor: `${PRIMARY_COLOR}cc`,
                },
              }}
              onClick={() => setShowSortDialog(false)}
              component={motion.div}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Apply
            </Button>
          </DialogActions>
        </StyledDialog>
      </div>

      <Footer />
    </>
  );
};

export default CategoryProductScreen;
